import { warn } from 'cc';
import StreamingPlayerAdapter, { CommonStreamingErrorName } from '../StreamingPlayerAdapter';
import StreamingError from '../../StreamingError';
import type { HlsBufferAppendedData, HlsConfig, HlsErrorData, HlsInstance } from './hls';
import { VideoElementEvents } from '../../../common/scripts/Toolkit/Components/VideoSprite/VideoSprite';
import { joinURL } from '../../../common/scripts/Toolkit/Utils/Utility';

export enum HlsStreamingErrorName {
    HLS_NOT_SUPPORTED = 'HLS_NOT_SUPPORTED',
    FATAL_ERROR = 'HLS_FATAL_ERROR',
}

/**
 * Adapter for an HLS manifest URL.
 *
 * hls.js is provided as a Cocos global JavaScript plugin next to this adapter.
 * Safari/iOS can use the native HTMLVideoElement HLS implementation.
 */
export default class HlsStreamingPlayerAdapter extends StreamingPlayerAdapter {
    private _hls: HlsInstance = null;
    private _playPromise: Promise<void> = null;
    private _playID = 0;
    private _timestampOffset = 0;

    constructor(public readonly videoElement: HTMLVideoElement) {
        super();
        videoElement.addEventListener('playing', this._onPlaying);
        videoElement.addEventListener('error', this._onVideoError);
        videoElement.addEventListener('resize', this.onResizeCallback);
    }

    public get muted(): boolean {
        return this.videoElement.muted;
    }
    public set muted(value: boolean) {
        this.videoElement.muted = value;
    }

    public get volume(): number {
        return this.videoElement.volume;
    }
    public set volume(value: number) {
        this.videoElement.volume = value;
    }

    public get controls(): boolean {
        return this.videoElement.controls;
    }
    public set controls(value: boolean) {
        this.videoElement.controls = value;
    }

    public get playsinline(): boolean {
        return this.videoElement.playsInline;
    }
    public set playsinline(value: boolean) {
        this.videoElement.playsInline = value;
    }

    public get loop(): boolean {
        return this.videoElement.loop;
    }
    public set loop(value: boolean) {
        this.videoElement.loop = value;
    }

    public get height(): number {
        return this.videoElement.videoHeight;
    }
    public get width(): number {
        return this.videoElement.videoWidth;
    }

    public get timestampOffset(): number {
        return this._timestampOffset;
    }

    public play(url: string): void {
        const playID = ++this._playID;
        this._destroyHls();
        this._timestampOffset = 0;
        this.videoElement.removeAttribute('src');
        this.videoElement.crossOrigin = 'anonymous';
        this.videoElement.setAttribute('crossorigin', 'anonymous');
        url = joinURL(url, 'index.m3u8');

        if (window.Hls.isSupported()) {
            this._playWithHls(url, playID);
            return;
        }
        warn(`hls.js is not supported`);
        if (this.videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            this.videoElement.addEventListener(VideoElementEvents.LOADED_METADATA, () => this._playVideo(playID), { once: true });
            this.videoElement.src = url;
            this.videoElement.load();
            return;
        }

        this.emit(HlsStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(HlsStreamingErrorName.HLS_NOT_SUPPORTED, url));
    }

    public resume(): Promise<void> {
        this._playPromise = this.videoElement.play().catch((err) => warn(err));
        return this._playPromise;
    }

    public pause(): void {
        this.videoElement.pause();
        this._playPromise = null;
    }

    public stop(): void {
        ++this._playID;
        this._playPromise = null;
        this._timestampOffset = 0;
        this._destroyHls();
        this.videoElement.pause();
        this.videoElement.removeAttribute('src');
        this.videoElement.load();
    }

    private readonly _onPlaying = () => {
        this.emit(HlsStreamingPlayerAdapter.EVENT_TYPE.Play);
    };

    private readonly _onVideoError = () => {
        const { error } = this.videoElement;
        if (error) {
            this.emit(HlsStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.OTHER_ERROR, error));
        }
    };

    private _playWithHls(url: string, playID: number): void {
        const hlsConfig: HlsConfig = {
            xhrSetup: (xhr) => {
                xhr.withCredentials = true;
            },
        };
        const hls = new window.Hls(hlsConfig);
        this._hls = hls;
        hls.on(window.Hls.Events.MEDIA_ATTACHED, () => {
            if (playID === this._playID) {
                hls.loadSource(url);
            }
        });
        hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            if (playID === this._playID) {
                this._playVideo(playID);
            }
        });

        const getAppendedOffset = (_event: string, { frag }: HlsBufferAppendedData) => {
            if (playID !== this._playID || frag.type !== 'main' || frag.sn === 'initSegment' || !frag.elementaryStreams?.video) {
                return;
            }
            const { start, elementaryStreams } = frag;
            const videoStartPTS = elementaryStreams.video.startPTS;
            if (!Number.isFinite(videoStartPTS)) {
                return;
            }
            this._timestampOffset = videoStartPTS - start;
            hls.off(window.Hls.Events.BUFFER_APPENDED, getAppendedOffset);
        };

        hls.on(window.Hls.Events.BUFFER_APPENDED, getAppendedOffset);
        hls.on(window.Hls.Events.ERROR, (_event, data) => this._onHlsError(hls, playID, data));
        hls.attachMedia(this.videoElement);
    }

    private _playVideo(playID: number): void {
        if (playID !== this._playID) {
            return;
        }
        this._playPromise = this.videoElement.play();
        this._playPromise.catch((error) => {
            if (playID === this._playID) {
                this.emit(HlsStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.OTHER_ERROR, error));
            }
        });
    }

    private _onHlsError(hls: HlsInstance, playID: number, data: HlsErrorData): void {
        if (playID !== this._playID || !data.fatal) {
            return;
        }
        if (data.type === window.Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
        }
        const { response } = data;
        const errorDetails = [
            response?.url && `url=${response.url}`,
            response?.code !== undefined && `status=${response.code}`,
            response?.text && `response=${response.text}`,
            data.error?.message && `error=${data.error.message}`,
        ]
            .filter(Boolean)
            .join(' ');
        warn(`HLS fatal error: ${data.type}/${data.details}${errorDetails ? ` ${errorDetails}` : ''}`);
        this.emit(HlsStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(HlsStreamingErrorName.FATAL_ERROR, data));
        this._destroyHls();
    }

    private _destroyHls(): void {
        if (!this._hls) {
            return;
        }
        this._hls.destroy();
        this._hls = null;
    }
}
